/**
 * PR-36S — Stadium Vibe State Engine.
 *
 * Pure mapper: takes the EXISTING `usePublicEmotionalSignal` output (the
 * `DominantKind` + intensity + score context) and the score differential,
 * and returns ONE `StadiumState` per tick. The engine NEVER polls, NEVER
 * fetches, NEVER opens a hub — it just labels the moment.
 *
 * The state is then written to `<html data-stadium-state="...">` via
 * `applyStadiumState`. CSS in `styles.css` consumes that attribute to
 * drive the ambient layer (aura intensity, tactical pulse, heat overlay,
 * comeback expansion) without any per-frame React work.
 *
 * Discipline:
 *   - All animations are CSS-only.
 *   - No state ever forces a layer above the score / radar (those layers
 *     stay in their own z-index lanes; this engine touches background
 *     ambient only).
 *   - `density` from edgeSafeFlags scales intensity globally; under hard
 *     edge safe / hidden tab / low battery the state degrades to "calm".
 */
import type { DominantKind } from "@/lib/viralIntel/emotionalIntensityCoordinator";

export type StadiumState =
  | "calm"
  | "tactical"
  | "momentum"
  | "pressure"
  | "danger"
  | "heat"
  | "comeback"
  | "domination"
  | "ultra_live";

export interface StadiumVibeInput {
  /** Dominant kind from EmotionalIntensityCoordinator (already arbitrated). */
  kind: DominantKind;
  /** 0..1 — directive intensity. */
  intensity: number;
  /** Live score context. */
  scoreHome: number;
  scoreAway: number;
  /** From edgeSafeFlags — when 0 we force calm. */
  density: number;
  /** True when the emotional coordinator asks for noise suppression. */
  suppressNoise?: boolean;
  /** True when the public hub considers the moment "ultra live" (Ultra Live).
   *  Optional — when omitted we derive from `kind === "viral_card"` + intensity.
   */
  ultraLive?: boolean;
}

export interface StadiumVibe {
  state: StadiumState;
  /** Effective intensity AFTER edgeSafe / suppressNoise scaling, 0..1. */
  intensity: number;
}

/** Pure mapping — same input → same output. */
export function deriveStadiumState(input: StadiumVibeInput): StadiumVibe {
  const density = clamp01(input.density);
  if (density <= 0.01) return { state: "calm", intensity: 0 };

  const diff = Math.abs(input.scoreHome - input.scoreAway);
  const trailing = Math.min(input.scoreHome, input.scoreAway);
  const leading = Math.max(input.scoreHome, input.scoreAway);
  const closeGame = diff <= 1;
  const dominating = diff >= 3 || (leading >= 3 && trailing === 0);
  const ultra =
    input.ultraLive === true ||
    (input.kind === "viral_card" && input.intensity >= 0.85);

  // Effective intensity — clamp + scale by density. Suppression cools by 25%.
  let eff = clamp01(input.intensity) * density;
  if (input.suppressNoise) eff *= 0.75;

  let state: StadiumState = "calm";

  if (ultra) {
    state = "ultra_live";
  } else if (input.kind === "idle") {
    state = closeGame && (input.scoreHome + input.scoreAway) >= 1 ? "tactical" : "calm";
  } else if (input.kind === "heat_window") {
    state = eff >= 0.7 ? "heat" : "pressure";
  } else if (input.kind === "contrarian") {
    state = "tactical";
  } else if (input.kind === "crowd") {
    state = eff >= 0.65 ? "pressure" : "tactical";
  } else if (input.kind === "story_arc") {
    if (trailing >= 1 && diff <= 1 && (input.scoreHome + input.scoreAway) >= 2) {
      state = "comeback";
    } else if (dominating) {
      state = "domination";
    } else if (eff >= 0.7) {
      state = "momentum";
    } else {
      state = "tactical";
    }
  } else if (input.kind === "viral_card") {
    // High-intensity viral that isn't ultra → danger flash.
    state = eff >= 0.75 ? "danger" : "momentum";
  }

  // Final clamp — danger / ultra live are allowed to ride higher, others
  // capped so the ambient never overwhelms readability.
  const ceiling = state === "ultra_live" ? 1 : state === "danger" || state === "heat" ? 0.9 : 0.75;
  eff = Math.min(eff, ceiling);

  return { state, intensity: round2(eff) };
}

/**
 * Write the derived state to the document root. Idempotent — only touches
 * the DOM when the value changed. Safe to call on every render.
 */
export function applyStadiumState(vibe: StadiumVibe): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const curState = root.getAttribute("data-stadium-state");
  if (curState !== vibe.state) {
    root.setAttribute("data-stadium-state", vibe.state);
  }
  const curIntensity = root.style.getPropertyValue("--stadium-intensity");
  const next = String(vibe.intensity);
  if (curIntensity.trim() !== next) {
    root.style.setProperty("--stadium-intensity", next);
  }
}

/** Reset to calm — used when the ambient host unmounts. */
export function resetStadiumState(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-stadium-state", "calm");
  root.style.setProperty("--stadium-intensity", "0");
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}